import React, { useState, useEffect } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import RoomPage from "./RoomPage";
import { doc, getDoc } from "firebase/firestore";
import db from "./firebase";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";


const WrappedRoomPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const location = useLocation();
  const { t } = useTranslation();

  const [roomName, setRoomName] = useState(t("untitledRoom"));
  const [estimationType, setEstimationType] = useState("fibonacci");
  const [mode, setMode] = useState<"withFacilitator" | "withoutFacilitator">("withFacilitator");

  const [roomType, setRoomType] = useState<"poker" | "bucket" | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [userId, setUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  useEffect(() => {
    let storedUserId = localStorage.getItem("userId");
    let storedUserName = localStorage.getItem("userName");

    if (!storedUserId) {
      storedUserId = Math.random().toString(36).substr(2, 9);
      localStorage.setItem("userId", storedUserId);
    }

    if (!storedUserName) {
      storedUserName = location.state?.name || prompt(t("userNamePrompt"))
        || `${t("defaultUserName")}-${storedUserId.slice(0, 4)}`;
      if (storedUserName) {
        localStorage.setItem("userName", storedUserName);
      }
    }

    setUserId(storedUserId);
    setUserName(storedUserName || t("defaultUserName"));
  }, [location.state]);

  useEffect(() => {
    (async () => {
      if (!roomId) return;
      setLoading(true);
      setNotFound(false);

      const roomRef = doc(db, "rooms", roomId);
      const roomSnap = await getDoc(roomRef);

      if (!roomSnap.exists()) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      const data = roomSnap.data() as any;
      if (data.roomName) setRoomName(data.roomName);
      if (data.estimationType) setEstimationType(data.estimationType);
      if (data.mode === "withFacilitator" || data.mode === "withoutFacilitator") {
        setMode(data.mode);
      }

      setRoomType(data.roomType ?? "poker");
      setLoading(false);
    })();
  }, [roomId]);

  if (!roomId || !userId || !userName) return <div>Error: Failed to load room</div>;
  if (loading) return null;
  if (notFound) return <div>Room not found</div>;

  if (roomType === "bucket") {
    return <Navigate to={`/bucket/${roomId}`} replace />;
  }

  return (
    <>
      <Helmet>
        <title>{roomName || `Комната ${roomId}`} — Pokerly</title>
        <meta
          name="description"
          content={`Комната для оценки задач в Pokerly: ${roomName || roomId}. Оценивайте вместе с командой.`}
        />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://test.pokerly.ru/room/${roomId}`} />
        <meta property="og:title" content={`${roomName || `Комната ${roomId}`} — Pokerly`} />
        <meta
          property="og:description"
          content={`Комната оценки задач: ${roomName || roomId}. Оценка задач с командой — быстро и удобно.`}
        />
        <meta property="og:image" content="https://test.pokerly.ru/og-image.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${roomName || `Комната ${roomId}`} — Pokerly`} />
        <meta
          name="twitter:description"
          content={`Оцените задачи вместе с командой в комнате ${roomName || roomId}.`}
        />
        <meta name="twitter:image" content="https://test.pokerly.ru/og-image.png" />
      </Helmet>

      <RoomPage
        roomId={roomId}
        userId={userId}
        userName={userName}
        roomName={roomName}
        estimationType={estimationType}
        onRoomNameChange={setRoomName}
        onEstimationTypeChange={setEstimationType}
        mode={mode}
      />
    </>
  );
};

export default WrappedRoomPage;
